import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IDeploymentConfigModel } from "@models/config/IDeploymentConfigModel";
import {
  APP_SETTINGS_SCHEMA_VERSION,
  K_DEV_SETTING_DEFAULTS,
  K_ROLE_MODEL_DEFAULTS,
  isReasoningEffort,
  normalizeRoleModelId,
  parseAppSettings,
  serializeAppSettings,
  type AppDevSettingKey,
  type AppRoleModelDto,
  type AppSettingsDto,
  type RoleModelConfig,
} from "@models/data/appSettingsDto";
import type { IAppSettingsModel } from "./IAppSettingsModel";

/**
 * `AppSettingsModel` — the cloud-backed implementation of
 * {@link IAppSettingsModel}. Headless: one cached document, a change emitter,
 * and a debounced read-modify-write. See the interface for the contracts and
 * `models/data/appSettingsDto.ts` for the document itself.
 */

/**
 * Coalescing window for writes. Long enough that flipping several switches in
 * a row publishes ONE document (which also removes the interleaved-write
 * problem entirely for the common case), short enough to feel immediate.
 */
const K_SAVE_DEBOUNCE_MS = 400;

export class AppSettingsModel implements IAppSettingsModel {
  readonly onChanged = new Emitter<void>();

  private m_auth: IAuthModel | null = null;
  private m_config: IDeploymentConfigModel | null = null;

  /** The cache. Empty until a load lands, so every getter falls back to defaults. */
  private m_doc: AppSettingsDto = {};
  private m_isLoaded = false;
  private m_lastError: string | null = null;

  private m_loadTask: Promise<void> | null = null;
  private m_saveTimer: ReturnType<typeof setTimeout> | null = null;
  private m_saveTask: Promise<boolean> | null = null;
  /** Resolvers for setters awaiting the write their change was folded into. */
  private m_pendingSaveWaiters: Array<(ok: boolean) => void> = [];

  get isLoaded(): boolean {
    return this.m_isLoaded;
  }

  get isSaving(): boolean {
    return this.m_saveTimer !== null || this.m_saveTask !== null;
  }

  get lastError(): string | null {
    return this.m_lastError;
  }

  get modifiedAt(): string | null {
    return typeof this.m_doc.modifiedAt === "string" ? this.m_doc.modifiedAt : null;
  }

  get modifiedBy(): string | null {
    return typeof this.m_doc.modifiedBy === "string" ? this.m_doc.modifiedBy : null;
  }

  // ---- Skills ------------------------------------------------------

  isSkillEnabled(skillId: string): boolean {
    const id = skillId?.trim().toLowerCase();
    if (!id) return true;
    // Absent reads as ENABLED — see the sparse-map note on the DTO.
    return this.m_doc.skills?.[id] !== false;
  }

  get disabledSkillIds(): ReadonlyArray<string> {
    const skills = this.m_doc.skills;
    if (!skills) return [];
    return Object.keys(skills).filter((id) => skills[id] === false);
  }

  setSkillEnabled(skillId: string, enabled: boolean): Promise<boolean> {
    const id = skillId?.trim().toLowerCase();
    if (!id) return Promise.resolve(false);
    if (this.isSkillEnabled(id) === enabled) return Promise.resolve(true);
    this.m_doc = { ...this.m_doc, skills: withEnabled(this.m_doc.skills, id, enabled) };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  // ---- Roles -------------------------------------------------------

  isRoleEnabled(roleId: string): boolean {
    const id = roleId?.trim().toLowerCase();
    if (!id) return true;
    return this.m_doc.roles?.[id] !== false;
  }

  get disabledRoleIds(): ReadonlyArray<string> {
    const roles = this.m_doc.roles;
    if (!roles) return [];
    return Object.keys(roles).filter((id) => roles[id] === false);
  }

  setRoleEnabled(roleId: string, enabled: boolean): Promise<boolean> {
    const id = roleId?.trim().toLowerCase();
    if (!id) return Promise.resolve(false);
    if (this.isRoleEnabled(id) === enabled) return Promise.resolve(true);
    this.m_doc = { ...this.m_doc, roles: withEnabled(this.m_doc.roles, id, enabled) };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  // ---- Per-role skill scope ----------------------------------------

  getRoleSkillOverride(roleId: string, skillId: string): boolean | null {
    const role = roleId?.trim().toLowerCase();
    const skill = skillId?.trim().toLowerCase();
    if (!role || !skill) return null;
    const stored = this.m_doc.roleSkills?.[role]?.[skill];
    // Absent is NOT "off" here — it is "ask the skill file". The tri-state is
    // the whole point of this member; collapsing it to a boolean would make an
    // unwritten document withdraw every skill from every role.
    return typeof stored === "boolean" ? stored : null;
  }

  setRoleSkillOverride(roleId: string, skillId: string, value: boolean | null): Promise<boolean> {
    const role = roleId?.trim().toLowerCase();
    const skill = skillId?.trim().toLowerCase();
    if (!role || !skill) return Promise.resolve(false);
    if (this.getRoleSkillOverride(role, skill) === value) return Promise.resolve(true);

    const roleSkills = { ...(this.m_doc.roleSkills ?? {}) };
    const entry = { ...(roleSkills[role] ?? {}) };
    if (value === null) delete entry[skill];
    else entry[skill] = value;
    // Prune the role's whole entry when nothing is left in it, so clearing the
    // last override returns the document to the shape it had before the first —
    // the same rule `withEnabled` applies one level up.
    if (Object.keys(entry).length === 0) delete roleSkills[role];
    else roleSkills[role] = entry;

    this.m_doc = { ...this.m_doc, roleSkills };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  get roleSkillOverrideSignature(): string {
    const roleSkills = this.m_doc.roleSkills;
    if (!roleSkills) return "";
    const parts: string[] = [];
    for (const role of Object.keys(roleSkills)) {
      const entry = roleSkills[role];
      for (const skill of Object.keys(entry)) {
        parts.push(`${role}:${skill}=${entry[skill] ? "1" : "0"}`);
      }
    }
    // Sorted so two documents with the same overrides in a different key order
    // compare equal — object key order is insertion order, and a read-modify-
    // write can reorder it.
    return parts.sort().join(",");
  }

  // ---- Core tool set -----------------------------------------------

  getCoreToolOverride(toolId: string): boolean | null {
    const id = toolId?.trim().toLowerCase();
    if (!id) return null;
    const stored = this.m_doc.coreTools?.[id];
    // Absent is "ask the build", not "not core" — see the DTO's note. Collapsing
    // the tri-state here would strip every role of its meta tools as soon as one
    // entry was written.
    return typeof stored === "boolean" ? stored : null;
  }

  setCoreToolOverride(toolId: string, value: boolean | null): Promise<boolean> {
    const id = toolId?.trim().toLowerCase();
    if (!id) return Promise.resolve(false);
    if (this.getCoreToolOverride(id) === value) return Promise.resolve(true);

    const coreTools = { ...(this.m_doc.coreTools ?? {}) };
    // Both values are stored; only `null` prunes. Unlike `withEnabled`, `true`
    // is a real deviation here — it makes a tool core that the build does not.
    if (value === null) delete coreTools[id];
    else coreTools[id] = value;

    this.m_doc = { ...this.m_doc, coreTools };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  get coreToolOverrides(): Readonly<Record<string, boolean>> {
    return this.m_doc.coreTools ?? {};
  }

  // ---- Per-role session model --------------------------------------

  getRoleModel(roleId: string): RoleModelConfig {
    const id = roleId?.trim().toLowerCase() ?? "";
    const def = K_ROLE_MODEL_DEFAULTS[id] ?? { model: null, reasoningEffort: null, voice: true };
    const stored = id ? this.m_doc.roleModels?.[id] : undefined;

    // Field-by-field merge: each field falls back to the role's default
    // independently, so a document that only pins the effort still gets the
    // role's default model (and vice versa).
    const model = normalizeRoleModelId(stored?.model) ?? def.model;
    const reasoningEffort = isReasoningEffort(stored?.reasoningEffort)
      ? stored.reasoningEffort
      : def.reasoningEffort;
    // Absent means the role's default, never `false` — the sparse rule this
    // document is built on. `sanitizeRoleModels` has already dropped anything
    // that is not a boolean, so a stored value here is trustworthy.
    const voice = typeof stored?.voice === "boolean" ? stored.voice : def.voice;
    return { model, reasoningEffort, voice };
  }

  setRoleModel(roleId: string, config: RoleModelConfig): Promise<boolean> {
    const id = roleId?.trim().toLowerCase() ?? "";
    if (!id) return Promise.resolve(false);
    const model = normalizeRoleModelId(config.model);
    const current = this.getRoleModel(id);
    if (
      current.model === model &&
      current.reasoningEffort === config.reasoningEffort &&
      current.voice === config.voice
    ) {
      return Promise.resolve(true);
    }

    const def = K_ROLE_MODEL_DEFAULTS[id] ?? { model: null, reasoningEffort: null, voice: true };
    const roleModels = { ...(this.m_doc.roleModels ?? {}) };
    if (
      model === def.model &&
      config.reasoningEffort === def.reasoningEffort &&
      config.voice === def.voice
    ) {
      // Back to the stock default: prune rather than store it, so a build
      // that changes a role's default is not overruled by old documents.
      delete roleModels[id];
    } else {
      // Both fields stored explicitly whenever the entry exists at all —
      // simpler to reason about than per-field sparseness inside the entry,
      // and unknown keys from a newer build's entry still survive the spread.
      const entry: AppRoleModelDto = { ...roleModels[id] };
      if (model === null) delete entry.model;
      else entry.model = model;
      if (config.reasoningEffort === null) delete entry.reasoningEffort;
      else entry.reasoningEffort = config.reasoningEffort;
      // Stored only when it DEVIATES from the role's default, unlike the two
      // above: a build that changes a role's shipped voice must not be
      // overruled by a document that wrote the old default out explicitly.
      if (config.voice === def.voice) delete entry.voice;
      else entry.voice = config.voice;
      roleModels[id] = entry;
    }
    this.m_doc = { ...this.m_doc, roleModels };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  // ---- Dev-panel settings ------------------------------------------

  getDevSetting(key: AppDevSettingKey): boolean {
    const stored = this.m_doc.devSettings?.[key];
    return typeof stored === "boolean" ? stored : K_DEV_SETTING_DEFAULTS[key];
  }

  setDevSetting(key: AppDevSettingKey, enabled: boolean): Promise<boolean> {
    if (this.getDevSetting(key) === enabled) return Promise.resolve(true);

    const devSettings = { ...(this.m_doc.devSettings ?? {}) };
    // Same sparse rule as the skill map: a value equal to the stock default is
    // stored as ABSENCE, so a build that changes a default is not overruled by
    // documents written before it.
    if (enabled === K_DEV_SETTING_DEFAULTS[key]) delete devSettings[key];
    else devSettings[key] = enabled;
    this.m_doc = { ...this.m_doc, devSettings };
    this.onChanged.emit();
    return this.scheduleSave();
  }

  // ---- Prompt overrides --------------------------------------------






  // ---- Lifecycle ---------------------------------------------------

  /**
   * @param auth used ONLY to stamp `modifiedBy` and to obtain the token a write
   * needs. The document belongs to the DEPLOYMENT, not to the account, so the
   * sign-in / sign-out subscriptions this used to carry are gone: a different
   * account does not have different settings, and clearing them on sign-out
   * would have withdrawn every skill and role from the login screen's own agent.
   */
  attachTransport(auth: IAuthModel, config: IDeploymentConfigModel): void {
    this.m_auth = auth;
    this.m_config = config;
    void this.ensureLoadedAsync();
  }

  ensureLoadedAsync(): Promise<void> {
    if (this.m_loadTask) return this.m_loadTask;
    if (this.m_isLoaded) return Promise.resolve();
    return this.reloadAsync();
  }

  reloadAsync(): Promise<void> {
    if (this.m_loadTask) return this.m_loadTask;
    this.m_loadTask = this.loadInternalAsync().finally(() => {
      this.m_loadTask = null;
    });
    return this.m_loadTask;
  }

  private async loadInternalAsync(): Promise<void> {
    const config = this.m_config;
    // No transport yet: stay inert on defaults. There is no sign-in gate any
    // more — a read is unauthenticated, so the deployment's settings are known
    // before anyone logs in.
    if (!config) return;

    try {
      const json = await config.getAsync("settings");
      // `null` is ABSENT — a fresh account, which is an empty document. The
      // storage layer throws on a real failure, so this cannot latch defaults
      // over settings that exist.
      this.m_doc = parseAppSettings(json);
      this.m_isLoaded = true;
      this.m_lastError = null;
    } catch (err) {
      // Keep serving defaults and say so. Not cached as "loaded", so the next
      // `ensureLoadedAsync` retries instead of latching the failure.
      this.m_lastError = errorText(err);
      console.warn("[AppSettingsModel] app-settings.json read failed:", err);
    }
    this.onChanged.emit();
  }

  // ---- Saving ------------------------------------------------------

  /**
   * Debounces, then publishes. Resolves with the outcome of the write this
   * change lands in, so a caller can report a failure against the toggle the
   * user just flipped.
   */
  private scheduleSave(): Promise<boolean> {
    const waiter = new Promise<boolean>((resolve) => {
      this.m_pendingSaveWaiters.push(resolve);
    });
    if (this.m_saveTimer !== null) clearTimeout(this.m_saveTimer);
    this.m_saveTimer = setTimeout(() => {
      this.m_saveTimer = null;
      void this.runSaveAsync();
    }, K_SAVE_DEBOUNCE_MS);
    return waiter;
  }

  private runSaveAsync(): Promise<boolean> {
    // Serialize: a second save while one is in flight must run AFTER it, or the
    // two read-modify-writes race on the same key.
    const previous = this.m_saveTask ?? Promise.resolve(true);
    const task = previous
      .catch(() => false)
      .then(() => this.saveInternalAsync())
      .finally(() => {
        if (this.m_saveTask === task) this.m_saveTask = null;
      });
    this.m_saveTask = task;
    return task;
  }

  private async saveInternalAsync(): Promise<boolean> {
    const waiters = this.m_pendingSaveWaiters;
    this.m_pendingSaveWaiters = [];
    const settle = (ok: boolean): boolean => {
      for (const resolve of waiters) resolve(ok);
      this.onChanged.emit();
      return ok;
    };

    const config = this.m_config;
    if (!config) {
      this.m_lastError = "No config transport — settings cannot be shared.";
      return settle(false);
    }

    try {
      // Read-modify-write against the fixed key. The re-read is what preserves
      // a newer build's unknown fields and any setting changed elsewhere since
      // this client last loaded; the local document wins only on the two maps
      // it owns.
      const remote = parseAppSettings(await config.getAsync("settings"));
      const merged: AppSettingsDto = {
        ...remote,
        ...this.m_doc,
        version: APP_SETTINGS_SCHEMA_VERSION,
        modifiedAt: new Date().toISOString(),
        modifiedBy: this.m_auth?.currentUser?.displayName ?? "unknown",
      };
      // Rejects when the host refuses the token; writing is the one half of this
      // model that still needs a signed-in user.
      await config.putAsync("settings", serializeAppSettings(merged));
      this.m_doc = merged;
      // A successful write proves the document; a client that had never managed
      // to READ it is now authoritative for it.
      this.m_isLoaded = true;
      this.m_lastError = null;
      return settle(true);
    } catch (err) {
      const message = errorText(err);
      this.m_lastError = message;
      console.warn("[AppSettingsModel] app-settings.json write failed:", err);
      settle(false);
      // Converge on what the server actually holds rather than leaving the
      // optimistic value showing as if it had been shared. If this re-read also
      // fails the optimistic value stays, with its own error explaining why.
      await this.reloadAsync();
      // The re-read clears `lastError` on success, but the WRITE still failed
      // and that is what the developer needs told — re-assert it.
      if (!this.m_lastError) {
        this.m_lastError = message;
        this.onChanged.emit();
      }
      return false;
    }
  }

}

/**
 * One entry of a sparse enabled-map, updated. Enabled is the default, so
 * enabling REMOVES the key rather than storing `true`: the map only ever records
 * deviations, which is what lets a newly shipped skill or role work without
 * anyone editing the document.
 */
function withEnabled(
  map: Record<string, boolean> | undefined,
  id: string,
  enabled: boolean,
): Record<string, boolean> {
  const next = { ...(map ?? {}) };
  if (enabled) delete next[id];
  else next[id] = false;
  return next;
}

function errorText(err: unknown): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  return String(err);
}
